package cz.vojtechhoranek.site.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.dom.GenericTag
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import cz.vojtechhoranek.site.localization.ProjectGalleryKind
import cz.vojtechhoranek.site.localization.LocalSiteText
import cz.vojtechhoranek.site.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private data class ProjectImage(
    val src: String,
    val width: Int,
    val height: Int,
    val english: String,
    val czech: String,
)

private val thesisImages = listOf(
    ProjectImage("/rovinka/welcome.webp", 720, 1533, "Welcome screen", "Úvodní obrazovka"),
    ProjectImage("/rovinka/patient-home.webp", 720, 1533, "Patient dashboard", "Přehled pacienta"),
    ProjectImage("/rovinka/patient-education.webp", 720, 1533, "Patient education", "Edukace pacientů"),
    ProjectImage("/rovinka/skin-check.webp", 720, 1533, "Guided skin check", "Průvodce kontrolou kůže"),
    ProjectImage("/rovinka/check-history.webp", 720, 1533, "Skin check history", "Historie kontrol kůže"),
    ProjectImage("/rovinka/watch-reminder.webp", 450, 690, "Repositioning reminder on a watch", "Připomínka polohování na hodinkách"),
    ProjectImage("/rovinka/staff-education.webp", 1440, 1056, "Education editor for staff", "Editor edukace pro personál"),
    ProjectImage("/rovinka/staff-report.webp", 1440, 1056, "Skin check report for staff", "Hlášení kontroly kůže pro personál"),
)

private val takeYourPillImages = listOf(
    ProjectImage("/take-your-pill-poster.webp", 1695, 2398, "Take Your Pill project poster", "Plakát projektu Take Your Pill"),
)

val ProjectGalleryToggleStyle = CssStyle {
    base {
        Modifier.margin(top = 1.25.cssRem).padding(topBottom = 0.6.cssRem, leftRight = 0.9.cssRem)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().outlineVariant)
            .borderRadius(0.5.cssRem).backgroundColor(colorMode.toSitePalette().background)
            .color(colorMode.toSitePalette().primary).fontSize(14.px)
            .styleModifier {
                property("font-family", "inherit")
                property("cursor", "pointer")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "0.5rem")
            }
    }
}

val ProjectGalleryDialogStyle = CssStyle {
    base {
        Modifier.padding(0.px).border(0.px).borderRadius(1.25.cssRem)
            .backgroundColor(colorMode.toSitePalette().background)
            .color(colorMode.toSitePalette().onBackground)
            .styleModifier {
                property("width", "min(1100px, calc(100vw - 24px))")
                property("height", "min(920px, calc(100dvh - 24px))")
                property("max-width", "none")
                property("max-height", "none")
                property("overflow", "hidden")
                property("box-shadow", "0 24px 100px #0008")
            }
    }
    cssRule("[open]") {
        Modifier.display(DisplayStyle.Grid)
            .styleModifier { property("grid-template-rows", "auto minmax(0, 1fr) auto") }
    }
    cssRule("::backdrop") {
        Modifier.styleModifier {
            property("background", "rgba(0, 0, 0, 0.78)")
            property("backdrop-filter", "blur(8px)")
        }
    }
    cssRule(" .gallery-header") {
        Modifier.display(DisplayStyle.Flex).padding(1.cssRem).gap(1.cssRem)
            .styleModifier { property("align-items", "center"); property("justify-content", "space-between") }
    }
    cssRule(" .gallery-title") { Modifier.fontSize(16.px).fontWeight(500) }
    cssRule(" .gallery-stage") {
        Modifier.position(Position.Relative).minHeight(0.px).minWidth(0.px)
            .margin(leftRight = 1.cssRem).borderRadius(0.75.cssRem)
            .backgroundColor(colorMode.toSitePalette().surfaceVariant)
            .styleModifier { property("overflow", "hidden") }
    }
    cssRule(" .gallery-stage img") {
        Modifier.display(DisplayStyle.Block).fillMaxWidth().fillMaxHeight()
            .styleModifier { property("object-fit", "contain") }
    }
    cssRule(" .gallery-status") {
        Modifier.position(Position.Absolute).fontSize(14.px)
            .styleModifier { property("inset", "0"); property("display", "grid"); property("place-items", "center") }
    }
    cssRule(" .gallery-footer") { Modifier.padding(1.cssRem) }
    cssRule(" .gallery-caption") {
        Modifier.display(DisplayStyle.Block).fontSize(14.px).lineHeight(1.5).fontWeight(500)
    }
    cssRule(" .gallery-controls") {
        Modifier.display(DisplayStyle.Flex).gap(0.5.cssRem).margin(top = 0.75.cssRem)
            .styleModifier { property("align-items", "center"); property("justify-content", "flex-end") }
    }
    cssRule(" .gallery-navigation") {
        Modifier.display(DisplayStyle.Flex).gap(0.5.cssRem)
            .styleModifier { property("align-items", "center") }
    }
    cssRule(" .gallery-counter") {
        Modifier.fontSize(13.px).color(colorMode.toSitePalette().onSurfaceVariant)
            .styleModifier { property("min-width", "3rem"); property("text-align", "center") }
    }
}

val ProjectGalleryControlStyle = CssStyle {
    base {
        Modifier.padding(topBottom = 0.6.cssRem, leftRight = 0.8.cssRem)
            .minHeight(44.px).minWidth(44.px).borderRadius(0.6.cssRem)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().outlineVariant)
            .backgroundColor(colorMode.toSitePalette().background)
            .color(colorMode.toSitePalette().onBackground).fontSize(14.px)
            .styleModifier { property("font-family", "inherit"); property("cursor", "pointer") }
    }
    cssRule(":hover") { Modifier.backgroundColor(colorMode.toSitePalette().surfaceVariant) }
    cssRule(":focus-visible") {
        Modifier.styleModifier { property("outline", "2px solid currentColor"); property("outline-offset", "3px") }
    }
}

@Composable
fun ProjectGallery(kind: ProjectGalleryKind) {
    var expanded by remember(kind) { mutableStateOf(false) }
    val czech = LocalSiteText.current.languageTag == "cs"
    val poster = kind == ProjectGalleryKind.TakeYourPill
    val images = if (poster) takeYourPillImages else thesisImages
    val galleryId = if (poster) "take-your-pill-gallery" else "thesis-gallery"
    Button(ProjectGalleryToggleStyle.toAttrs {
        attr("type", "button")
        attr("aria-haspopup", "dialog")
        attr("aria-expanded", expanded.toString())
        onClick { expanded = true }
    }) {
        SiteIcon(if (poster) SiteIconKind.Poster else SiteIconKind.Gallery)
        Text(when {
            poster && czech -> "Zobrazit plakát"
            poster -> "Show poster"
            czech -> "Zobrazit ukázky aplikace"
            else -> "Show app screenshots"
        })
    }
    // Create the dialog and image only after the visitor opens the gallery.
    if (expanded) {
        GalleryOverlay(images, galleryId, if (poster) "Take Your Pill" else "Rovinka", czech) { expanded = false }
    }
}

@Composable
private fun GalleryOverlay(
    images: List<ProjectImage>,
    galleryId: String,
    title: String,
    czech: Boolean,
    onClose: () -> Unit,
) {
    var selected by remember { mutableStateOf(0) }
    val navigate: (Int) -> Unit = { direction ->
        selected = (selected + direction + images.size) % images.size
    }
    val image = images[selected]
    val caption = if (czech) image.czech else image.english

    GenericTag<HTMLElement>("dialog", attrs = ProjectGalleryDialogStyle.toAttrs {
        id(galleryId)
        attr("aria-labelledby", "$galleryId-title")
        onClick { event ->
            val bounds = (event.currentTarget as HTMLElement).getBoundingClientRect()
            if (event.target == event.currentTarget &&
                (event.clientX < bounds.left || event.clientX > bounds.right ||
                    event.clientY < bounds.top || event.clientY > bounds.bottom)) onClose()
        }
        onKeyDown { event ->
            if (images.size > 1) {
                when (event.key) {
                    "ArrowLeft" -> { event.preventDefault(); navigate(-1) }
                    "ArrowRight" -> { event.preventDefault(); navigate(1) }
                }
            }
        }
    }) {
        DisposableEffect(Unit) {
            val dialog = scopeElement
            val opener = document.activeElement as? HTMLElement
            val body = document.body
            val previousOverflow = body?.style?.getPropertyValue("overflow") ?: ""
            val cancel: (Event) -> Unit = { it.preventDefault(); onClose() }
            dialog.addEventListener("cancel", cancel)
            body?.style?.setProperty("overflow", "hidden")
            dialog.asDynamic().showModal()
            onDispose {
                dialog.removeEventListener("cancel", cancel)
                dialog.asDynamic().close()
                body?.style?.setProperty("overflow", previousOverflow)
                opener?.focus()
            }
        }
        Div({ classes("gallery-header") }) {
            Span({ id("$galleryId-title"); classes("gallery-title") }) { Text(title) }
            Button(ProjectGalleryControlStyle.toAttrs {
                attr("type", "button")
                attr("autofocus", "")
                attr("aria-label", if (czech) "Zavřít galerii" else "Close gallery")
                onClick { onClose() }
            }) { SiteIcon(SiteIconKind.Close) }
        }
        key(image.src) {
            var loaded by remember { mutableStateOf(false) }
            var failed by remember { mutableStateOf(false) }
            Div({ classes("gallery-stage") }) {
                Img(src = image.src, alt = caption, attrs = {
                    attr("width", image.width.toString())
                    attr("height", image.height.toString())
                    attr("decoding", "async")
                    ref { element ->
                        val onLoad: (Event) -> Unit = { loaded = true }
                        val onError: (Event) -> Unit = { failed = true }
                        element.addEventListener("load", onLoad)
                        element.addEventListener("error", onError)
                        if (element.complete && element.naturalWidth > 0) loaded = true
                        onDispose {
                            element.removeEventListener("load", onLoad)
                            element.removeEventListener("error", onError)
                        }
                    }
                })
                if (!loaded) {
                    Div({ classes("gallery-status"); attr("role", "status") }) {
                        Text(if (failed) {
                            if (czech) "Obrázek se nepodařilo načíst." else "The image could not load."
                        } else {
                            if (czech) "Načítání…" else "Loading…"
                        })
                    }
                }
            }
        }
        Div({ classes("gallery-footer") }) {
            Span({ classes("gallery-caption"); attr("aria-live", "polite"); attr("aria-atomic", "true") }) { Text(caption) }
            if (images.size > 1) {
                Div({ classes("gallery-controls") }) {
                    Div({ classes("gallery-navigation") }) {
                        Button(ProjectGalleryControlStyle.toAttrs {
                            attr("type", "button")
                            attr("aria-label", if (czech) "Předchozí obrázek" else "Previous image")
                            onClick { navigate(-1) }
                        }) { Text("←") }
                        Span({ classes("gallery-counter") }) { Text("${selected + 1} / ${images.size}") }
                        Button(ProjectGalleryControlStyle.toAttrs {
                            attr("type", "button")
                            attr("aria-label", if (czech) "Další obrázek" else "Next image")
                            onClick { navigate(1) }
                        }) { Text("→") }
                    }
                }
            }
        }
    }
}
